import { useState, useRef, useEffect, useMemo } from 'react';

/**
 * A lightweight searchable dropdown (combobox).
 *
 * Behaves like a native <select> for the parent (value + onChange with the
 * selected string), but lets the user type to filter the list — handy when
 * there are many options (e.g. a long instructor list). Frontend-only: it
 * still emits a plain string value, so it's a drop-in replacement.
 *
 * @param {string} value            currently selected option ('' = none)
 * @param {(val: string) => void} onChange
 * @param {string[]} options        list of selectable strings
 * @param {string} [placeholder]    shown when nothing is selected
 * @param {string} [emptyLabel]     label for the "clear selection" row
 */
const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  emptyLabel = 'None',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.toLowerCase().includes(q));
  }, [options, query]);

  // Close on outside click.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus the search box when opening.
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const open = () => {
    setHighlight(0);
    setIsOpen(true);
  };

  const select = (option) => {
    onChange(option);
    setIsOpen(false);
    setQuery('');
  };

  // The "clear selection" row only shows when not actively searching. When it's
  // visible it occupies index 0 and the options shift down by one (offset = 1).
  const showClear = !query.trim();
  const offset = showClear ? 1 : 0;

  const handleKeyDown = (e) => {
    const total = filtered.length + offset;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % total);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + total) % total);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showClear && highlight === 0) {
        select('');
      } else {
        select(filtered[highlight - offset]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger / display */}
      <button
        type="button"
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        className="w-full flex items-center justify-between border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-left bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
      >
        <span className={value ? 'text-gray-800' : 'text-gray-400'}>
          {value || placeholder}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlight(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search..."
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
          <ul className="max-h-48 overflow-y-auto py-1" role="listbox">
            {/* Clear / none option — only when not actively searching */}
            {showClear && (
              <li
                role="option"
                aria-selected={!value}
                onMouseEnter={() => setHighlight(0)}
                onClick={() => select('')}
                className={`px-3 py-1.5 text-sm cursor-pointer ${
                  highlight === 0 ? 'bg-orange-50' : ''
                } text-gray-500 italic`}
              >
                {emptyLabel}
              </li>
            )}
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400">No matches</li>
            ) : (
              filtered.map((option, idx) => (
                <li
                  key={option}
                  role="option"
                  aria-selected={value === option}
                  onMouseEnter={() => setHighlight(idx + offset)}
                  onClick={() => select(option)}
                  className={`px-3 py-1.5 text-sm cursor-pointer ${
                    highlight === idx + offset ? 'bg-orange-50' : ''
                  } ${value === option ? 'font-semibold text-orange-600' : 'text-gray-800'}`}
                >
                  {option}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
